import type { DragData, DropEdge } from './drag-and-drop.types';

/**
 * Calculates whether the drop indicator should be hidden based on the source and target positions
 */
export function shouldHideDropIndicator(
  sourceIndex: number,
  targetIndex: number,
  edge: DropEdge,
  direction: 'vertical' | 'horizontal' | 'both'
): boolean {
  const isItemBeforeSource = targetIndex === sourceIndex - 1;
  const isItemAfterSource = targetIndex === sourceIndex + 1;

  if (direction === 'vertical') {
    return (isItemBeforeSource && edge === 'bottom') || (isItemAfterSource && edge === 'top');
  } else if (direction === 'horizontal') {
    return (isItemBeforeSource && edge === 'right') || (isItemAfterSource && edge === 'left');
  } else if (direction === 'both') {
    return (
      (isItemBeforeSource && (edge === 'bottom' || edge === 'right')) ||
      (isItemAfterSource && (edge === 'top' || edge === 'left'))
    );
  }

  return false;
}

/**
 * Calculates the destination index for a drop operation based on edge and direction
 */
export function calculateDestinationIndex(
  sourceIndex: number,
  targetIndex: number,
  edge: DropEdge,
  direction: 'vertical' | 'horizontal' | 'both'
): number {
  // Determine if we're moving forward or backward in the list
  const isMovingForward = sourceIndex < targetIndex;

  // Map edges to their corresponding "after" edges for each direction
  const afterEdges: Record<string, DropEdge[]> = {
    vertical: ['bottom'],
    horizontal: ['right'],
    both: ['bottom', 'right'],
  };

  const beforeEdges: Record<string, DropEdge[]> = {
    vertical: ['top'],
    horizontal: ['left'],
    both: ['top', 'left'],
  };

  const allowedAfterEdges = afterEdges[direction] || [];
  const allowedBeforeEdges = beforeEdges[direction] || [];

  // If dropping after the target (bottom/right), adjust index based on movement direction
  if (allowedAfterEdges.includes(edge)) {
    return isMovingForward ? targetIndex : targetIndex + 1;
  }

  // If dropping before the target (top/left), adjust index based on movement direction
  if (allowedBeforeEdges.includes(edge)) {
    return isMovingForward ? targetIndex - 1 : targetIndex;
  }

  // Fallback to target index if edge is not recognized
  return targetIndex;
}

/**
 * Creates drag data object for drag operations
 */
export function createDragData(itemId: string, index: number, instanceId: symbol): DragData {
  return {
    type: 'drag-drop-item',
    itemId,
    index,
    instanceId,
  };
}
